import { css, cx } from "@emotion/css";
import { IconButton } from "@octopusdeploy/design-system-components";
import { text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { DeploymentTargetResource } from "@octopusdeploy/octopus-server-client";
import type { KubernetesLiveStatusResource, KubernetesLiveStatusResourceObject } from "@octopusdeploy/octopus-server-client/src/resources/kubernetesLiveStatusResource";
import { groupBy } from "lodash";
import * as React from "react";
import { DegradedIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/DegradedIcon";
import { HealthyIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/HealthyIcon";
import { OutOfSyncIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/OutOfSyncIcon";
import { ProgressingIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/ProgressingIcon";
import { UnknownIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/UnknownIcon";
import { K8sIcon } from "~/areas/projects/components/Observability/k8s_icon";
import { DataTable, DataTableBody, DataTableHeader, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
type LiveStatusTableProps = {
    kubernetesLiveStatusResources: KubernetesLiveStatusResource;
    deploymentTargets: DeploymentTargetResource[];
    environmentId: string;
    projectId: string;
    tenantIdOrUntenanted: string;
};
export function LiveStatusTable({ deploymentTargets, environmentId, kubernetesLiveStatusResources, projectId, tenantIdOrUntenanted }: LiveStatusTableProps) {
    const kubernetesLiveStatusQuery = useQuery((repository) => repository.KubernetesLiveStatus.getForApplicationInstance(projectId, environmentId, tenantIdOrUntenanted), [environmentId, projectId, tenantIdOrUntenanted], "Fetch K8s Live Data", {
        initialResult: kubernetesLiveStatusResources,
        refetchIntervalInMs: 1000,
    });
    const deploymentTargetQuery = useQuery((repository) => repository.Machines.all({ ids: kubernetesLiveStatusQuery.result.Resources.map((r) => r.MachineId) }), [kubernetesLiveStatusQuery.result], "Fetch K8s Live Data", {
        initialResult: deploymentTargets,
        refetchIntervalInMs: 1000,
    });
    const machineIndexedKubernetesObjects = groupBy(kubernetesLiveStatusQuery.result.Resources, (r) => r.MachineId);
    return (<DataTable title="Live Objects">
            <DataTableHeader>
                <DataTableRow>
                    <HeaderCell text="name"/>
                    <HeaderCell text="kind"/>
                    <HeaderCell text="ready"/>
                    <HeaderCell text="namespace"/>
                    <HeaderCell text="status"/>
                </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
                {deploymentTargetQuery.result.map((deploymentTargetResource, index) => (<MachineAndChildren target={deploymentTargetResource} k8sResources={machineIndexedKubernetesObjects[deploymentTargetResource.Id]} startExpanded={deploymentTargetQuery.result.length === 1}/>))}
            </DataTableBody>
        </DataTable>);
}
function HeaderCell(props: {
    text: string;
}) {
    return (<DataTableRowColumn>
            <h2 className={styles.headerRow}>{props.text}</h2>
        </DataTableRowColumn>);
}
type MachineAndChildrenProps = {
    target: DeploymentTargetResource;
    k8sResources: KubernetesLiveStatusResourceObject[];
    startExpanded: boolean;
};
function MachineAndChildren({ target, k8sResources, startExpanded }: MachineAndChildrenProps) {
    const [open, setOpen] = React.useState(startExpanded);
    return (<>
            <DataTableRow key={target.Id} className={styles.machineRow}>
                <DataTableRowColumn colSpan={5} fullWidth={true} fullHeight={false}>
                    <div className={styles.multiItemCellFlexContainer}>
                        <CollapseIconOrSpacer showCollapse={k8sResources.length > 0} isOpen={open} onClick={() => setOpen(!open)}/>
                        <p className={styles.machineText}>{target.Name}</p>
                    </div>
                </DataTableRowColumn>
            </DataTableRow>
            {open && k8sResources.map((resource, index) => <Row resource={resource} key={`${target.Id}child${index}`} indentLevel={1}/>)}
        </>);
}
function Row({ indentLevel, key, resource }: {
    indentLevel: number;
    key: string;
    resource: KubernetesLiveStatusResourceObject;
}) {
    const [open, setOpen] = React.useState(false);
    return (<>
            <DataTableRow key={key} displayNoBorder={true}>
                <DataTableRowColumn fullWidth={true} fullHeight={false} className={styles.centredTableContent}>
                    <NameAndSymbols resource={resource} indentLevel={indentLevel} isOpen={open} onClick={() => setOpen(!open)}/>
                </DataTableRowColumn>
                <TextCell text={resource.Kind}/>
                <TextCell text=""/>
                <TextCell text={resource.Namespace}/>
                <DataTableRowColumn fullHeight={false} className={styles.centredTableContent}>
                    <div className={styles.multiItemCellFlexContainer}>
                        {GetTrafficLight(resource)}
                        <p className={cx(styles.tableCellText, styles.leftPad)}>{resource.Status}</p>
                    </div>
                </DataTableRowColumn>
            </DataTableRow>
            {open && resource.Children.map((resource, index) => <Row resource={resource} key={`${key}child${index}`} indentLevel={indentLevel + 1}/>)}
        </>);
}
function TextCell({ text }: {
    text: string;
}) {
    return (<DataTableRowColumn fullHeight={false} className={styles.centredTableContent}>
            <p className={styles.tableCellText}>{text}</p>
        </DataTableRowColumn>);
}
function NameAndSymbols({ indentLevel, isOpen, onClick, resource }: {
    resource: KubernetesLiveStatusResourceObject;
    indentLevel: number;
    isOpen: boolean;
    onClick: () => void;
}) {
    const pixelsPerIndent = 40;
    const indent = pixelsPerIndent * indentLevel;
    const hasChildNodes = resource.Children.length > 0;
    return (<div className={cx(styles.multiItemCellFlexContainer, styles.indented(indent))}>
            <CollapseIconOrSpacer showCollapse={hasChildNodes} isOpen={isOpen} onClick={onClick}/>
            <div className={cx(styles.multiItemCellFlexContainer, styles.leftPad)}>
                <K8sIcon resourceType={resource.Kind}/>
                <p className={cx(styles.tableCellText, styles.leftPad)}>{resource.Name}</p>
            </div>
        </div>);
}
function CollapseIconOrSpacer({ isOpen, onClick, showCollapse }: {
    showCollapse: boolean;
    isOpen: boolean;
    onClick: () => void;
}) {
    const iconSize = 48;
    if (showCollapse) {
        return (<div className={styles.iconPadding}>
                <IconButton onClick={onClick} icon={isOpen ? "Expand" : "ChevronRight"}/>
            </div>);
    }
    else {
        return <div className={styles.iconSpacer(iconSize)}/>;
    }
}
const healthyStatuses = ["Healthy", "Running", "InSync"];
function GetTrafficLight(resource: KubernetesLiveStatusResourceObject) {
    if (resource.Status === "Progressing") {
        return <ProgressingIcon />;
    }
    else if (healthyStatuses.includes(resource.Status) || resource.Status.includes("Available") || resource.Status.includes("Ready")) {
        return <HealthyIcon />;
    }
    else if (resource.Status === "OutOfSync" || resource.Status === "Pending") {
        return <OutOfSyncIcon />;
    }
    else if (resource.Status === "Unhealthy") {
        return <DegradedIcon />;
    }
    return <UnknownIcon />;
}
const styles = {
    machineRow: css({
        background: themeTokens.color.background.secondary.default,
    }),
    machineText: css({
        font: text.regular.default.medium,
        margin: 0,
    }),
    multiItemCellFlexContainer: css({
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "0px",
        height: "100%",
    }),
    tableCellText: css({
        font: text.regular.default.medium,
        display: "inline-block",
        margin: 0,
    }),
    leftPad: css({
        paddingLeft: "8px",
    }),
    centredTableContent: css({
        "&&": { verticalAlign: "middle" },
    }),
    headerRow: css({
        color: themeTokens.color.text.secondary,
        font: text.heading.xSmall,
        margin: 0,
        textTransform: "uppercase",
    }),
    iconPadding: css({
        paddingLeft: "12px",
        paddingRight: "12px",
    }),
    indented(pixels: number) {
        return css({ paddingLeft: pixels });
    },
    iconSpacer(width: number) {
        return css({ width: width });
    },
};
